import { useEffect, useMemo, useState } from 'react';
import { Routes, useLocation } from 'react-router';
import { getRouter } from './utils/router';
import { IRouteObject } from './router/interfaces';
import './index.scss';
import { momentRoutes } from './moment/routes/router-with-moment-v2.tsx';
import { BridgeProvider } from './moment/bridge/bridge-provider.tsx';
import bridgeEventPipe from './moment/bridge/bridge-event-pipe.ts';
import { UserTracker } from './pages/Root/components/UserTracker/UserTracker.tsx';
import { ThemeProvider } from '@moment/context/themeContextProvider.tsx';
import { AnalyticsProvider } from './moment/analytics/AnalyticsProvider.tsx';
import {
  RuntimeConfig,
  WithCommonPageData,
} from './pages/Root/ContextWrapper/WithInterest/WithCommonPageData.tsx';
import { AdsLayout } from '@moment/layouts/Ads/AdsLayout.tsx';
import { ContentDismissalProvider } from '@moment/analytics/ContentDismissalProvider.tsx';
import { useContentDismissalTracking } from '@moment/analytics/useContentDismissalTracking.ts';

import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';

bridgeEventPipe.init();

export interface AppProps {
  // in SSR env - this should be populated with the resolved partner.
  // on client side this should be null
  runtimeConfig: RuntimeConfig | null;
  dynamicRoutes?: IRouteObject[];
}

function AppContent(props: AppProps) {
  const location = useLocation();
  const routesToRender = useMemo(() => {
    // On the client, dynamicRoutes come from props passed by client-entrypoint.
    // On the server, they are also passed via props.
    // Prepend dynamic routes to give them matching priority over static routes.
    const allRoutes = [...(props.dynamicRoutes || []), ...momentRoutes];
    return allRoutes;
  }, [props.dynamicRoutes]);

  const routeElements = useMemo(() => {
    return routesToRender.map((item) => {
      return getRouter(item);
    });
  }, [routesToRender]);

  const [logs, setLogs] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(true); // Controls popup visibility
  const analyticsService = useAnalytics();
  const contentDismissalService = useContentDismissalTracking(analyticsService);

  useEffect(() => {
    const debugData = document.getElementById('debug-data')?.textContent;
    if (debugData) {
      setLogs(JSON.parse(debugData));
    }
  }, []);

  const classList = useMemo(() => ['fotoscape-app'], []);

  return (
    <ContentDismissalProvider service={contentDismissalService}>
      <WithCommonPageData runtimeConfig={props.runtimeConfig}>
        <AdsLayout>
          <UserTracker>
            <div className={classList.join(' ')}>
              {logs && location.search.includes('debug') && isOpen && (
                <div
                  style={{
                    position: 'fixed',
                    bottom: 10,
                    right: 10,
                    background: '#fff',
                    padding: 10,
                    border: '1px solid #ccc',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    zIndex: 9999, // High z-index to stay above other elements
                    width: '400px', // Consistent width
                    maxHeight: '300px', // Fixed height with scroll
                    overflow: 'hidden', // Prevent container overflow
                  }}
                >
                  {/* Header with title and close button */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 5,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>Debug Logs</h3>
                    <button
                      onClick={() => {
                        return setIsOpen(false);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        cursor: 'pointer',
                      }}
                    >
                      &times; {/* Unicode "×" for the close button */}
                    </button>
                  </div>

                  {/* Scrollable log content */}
                  <pre
                    style={{
                      margin: 0,
                      padding: 10,
                      background: '#f5f5f5',
                      borderRadius: 4,
                      overflow: 'auto', // Scrollbar appears if content overflows
                      maxHeight: '250px', // Leaves space for the header
                      whiteSpace: 'pre-wrap', // Preserves newlines and wraps text
                    }}
                  >
                    {JSON.stringify(logs.logs, null, 2)}
                  </pre>
                </div>
              )}
              <div id="dialog" data-testid="dialog" />
              <Routes>
                {routeElements?.length > 0
                  ? routeElements
                  : momentRoutes.map((r) => getRouter(r))}
              </Routes>
            </div>
          </UserTracker>
        </AdsLayout>
      </WithCommonPageData>
    </ContentDismissalProvider>
  );
}

function App(props: AppProps) {
  return (
    <BridgeProvider>
      <ThemeProvider>
        <AnalyticsProvider>
          <AppContent {...props} />
        </AnalyticsProvider>
      </ThemeProvider>
    </BridgeProvider>
  );
}

export default App;
